// § 3 · Processing
// "What happens to data after it arrives?"
//
// Operator's mental model anchor for the trigger pipeline. Empty state
// surfaces three quick-pick action templates so first-time operators
// have a clear next step.

import { useEffect, useState } from 'react';
import type { Source, Trigger } from '@datahub/shared';
import { archiveTrigger, listTriggers, patchTrigger } from '../../api';
import { useToast } from '../Toast';
import { SectionShell, EmptyState } from './SectionShell';
import { NewTriggerDialog } from '../dialogs/NewTriggerDialog';

export function ProcessingPanelV2({ source }: { source: Source }): React.ReactElement {
  const toast = useToast();
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<Trigger | null>(null);

  const sourceId = source.source_id;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listTriggers(sourceId)
      .then(t => { if (alive) { setTriggers(t); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sourceId]);

  async function togglePause(t: Trigger) {
    const next = t.status === 'paused' ? 'active' : 'paused';
    try {
      const updated = await patchTrigger(sourceId, t.trigger_id, { status: next });
      setTriggers(ts => ts.map(x => x.trigger_id === t.trigger_id ? updated : x));
      toast.show(`${t.name} ${next === 'paused' ? 'paused' : 'resumed'}.`, { kind: 'success' });
    } catch (e) {
      toast.show(`Toggle failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }

  async function deleteTrigger(t: Trigger) {
    if (!window.confirm(`Archive trigger "${t.name}"?\n\nDerivatives stay in BQ; replay won't re-fire it.`)) return;
    try {
      await archiveTrigger(sourceId, t.trigger_id);
      setTriggers(ts => ts.filter(x => x.trigger_id !== t.trigger_id));
      toast.show(`Trigger archived.`, { kind: 'success' });
    } catch (e) {
      toast.show(`Archive failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }

  return (
    <SectionShell
      id="processing"
      title="Processing"
      subtitle="What happens to events after they arrive"
      headerExtra={
        triggers.length > 0 && (
          <button className="btn sm" onClick={() => setShowNew(true)}>+ trigger</button>
        )
      }
    >
      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>
      ) : triggers.length === 0 ? (
        <EmptyState
          icon="⚙"
          title="No triggers yet"
          body="Triggers transform, validate, or promote events. Pick a starting point or build from scratch."
          action={
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, maxWidth: 600, margin: '0 auto' }}>
              <QuickStart label="Validate" desc="Drop events missing required fields" onClick={() => setShowNew(true)} />
              <QuickStart label="Enrich" desc="Fetch external context, merge into payload" onClick={() => setShowNew(true)} />
              <QuickStart label="Promote" desc="Stream events into a typed BigQuery table" onClick={() => setShowNew(true)} />
            </div>
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {triggers.map(t => (
            <TriggerRow
              key={t.trigger_id}
              trigger={t}
              onToggle={() => void togglePause(t)}
              onEdit={() => setEditing(t)}
              onArchive={() => void deleteTrigger(t)}
            />
          ))}
        </div>
      )}

      {showNew && (
        <NewTriggerDialog
          sourceId={sourceId}
          views={[]}
          onCancel={() => setShowNew(false)}
          onSave={(t) => { setTriggers(ts => [...ts, t]); setShowNew(false); }}
        />
      )}
      {editing && (
        <NewTriggerDialog
          sourceId={sourceId}
          views={[]}
          editing={editing}
          onCancel={() => setEditing(null)}
          onSave={(updated) => { setTriggers(ts => ts.map(x => x.trigger_id === updated.trigger_id ? updated : x)); setEditing(null); }}
        />
      )}
    </SectionShell>
  );
}

function TriggerRow({ trigger, onToggle, onEdit, onArchive }: { trigger: Trigger; onToggle: () => void; onEdit: () => void; onArchive: () => void }): React.ReactElement {
  const isPaused = trigger.status === 'paused';
  const isFail = trigger.last_run_status === 'failure';
  // Pause attribution — stamped on every status change in PATCH route.
  // Renders only when status === 'paused' AND we have audit info.
  const pausedAttribution = isPaused && trigger.last_status_change_at ? (
    <span title={`Paused ${new Date(trigger.last_status_change_at).toLocaleString()}${trigger.last_status_change_by ? ' by ' + trigger.last_status_change_by : ''}${trigger.last_status_change_reason ? ' — ' + trigger.last_status_change_reason : ''}`}
          style={{ color: 'oklch(0.55 0.13 60)' }}>
      paused {fmtRelativeShort(trigger.last_status_change_at)}
      {trigger.last_status_change_by && trigger.last_status_change_by !== 'api' && <> by {trigger.last_status_change_by}</>}
    </span>
  ) : null;
  return (
    <div style={{
      padding: '10px 12px',
      background: 'var(--bg)',
      border: `1px solid ${isFail ? 'var(--danger)' : 'var(--line)'}`,
      borderLeft: `3px solid ${isFail ? 'var(--danger)' : isPaused ? 'oklch(0.65 0.13 60)' : 'var(--accent)'}`,
      borderRadius: 6,
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{
            padding: '1px 6px', fontFamily: 'var(--mono)', fontSize: 10,
            background: 'var(--bg-sunken)', borderRadius: 3,
            color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.08em',
          }}>{trigger.action.type}</span>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{trigger.name}</span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
          v{trigger.version}
          {trigger.view_id && <> · scoped to view <code style={{ fontFamily: 'var(--mono)' }}>{trigger.view_id.slice(0, 12)}…</code></>}
          {trigger.last_run_at && <> · last run {new Date(trigger.last_run_at).toLocaleString()}</>}
          {pausedAttribution && <> · {pausedAttribution}</>}
        </div>
      </div>
      <button className="btn sm ghost" onClick={onToggle} title={isPaused ? 'Resume' : 'Pause'}>{isPaused ? '▶' : '⏸'}</button>
      <button className="btn sm ghost" onClick={onEdit} title="Edit">edit</button>
      <button className="btn sm ghost" onClick={onArchive} title="Archive trigger" style={{ color: 'var(--danger)' }}>×</button>
    </div>
  );
}

function fmtRelativeShort(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60000) return 'just now';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function QuickStart({ label, desc, onClick }: { label: string; desc: string; onClick: () => void }): React.ReactElement {
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left', padding: 12, background: 'var(--bg)', border: '1px solid var(--line)',
        borderRadius: 6, cursor: 'pointer', color: 'var(--ink)',
      }}
    >
      <div style={{ fontSize: 12, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)' }}>{label}</div>
      <div style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.4 }}>{desc}</div>
    </button>
  );
}
